import { useFormatters } from '@/hooks/useFormatters';
import { useGlobalModal } from '@/hooks/useGlobalModal';
import DeviceNameSpan from '@components/deviceNameSpan';
import GlobalModal from '@components/GlobalModal';
import ConfirmationModal from '@components/modal/ConfirmationModal';
import ModalActions from '@components/modal/ModalActions';
import ModalContent from '@components/modal/ModalContent';
import ModalTitle from '@components/modal/ModalTitle';
import { Access } from '@features/authorization';
import { formatPosition } from '@features/gateCommon/utils/formatPosition';
import { getMovementFragmentData } from '@features/gateCommon/utils/getMovementFragmentData';
import { msg, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { useCallback, useEffect, useState, type FC } from 'react';
import { GREEN } from '../constants';
import { useMoveGate } from '../hooks/useMoveGate';
import { LastPosition } from './LastPosition';
import { MoveGateForm } from './MoveGateForm';
import { OperatingState } from './OperatingState';
import { Scenario } from './Scenario';

export const MoveGateModal: FC = () => {
  const { _ } = useLingui();
  const { formatMetricValue } = useFormatters();
  const { globalModalProps, state, onClose } = useGlobalModal('moveGateModal');
  const { setModalState: setOperationModal } = useGlobalModal('gateOperationModal');
  const [isValid, setIsValid] = useState(false);
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [initialPosition, setInitialPosition] = useState(0);
  const [position, setPosition] = useState('');

  const { moveGate, loading } = useMoveGate({
    onCompleted({ requestGateMovement }) {
      setOpenConfirmation(false);
      const movement = getMovementFragmentData(requestGateMovement.movement);
      onClose();
      setOperationModal({
        open: true,
        movementId: movement.id,
      });
    },
  });

  const gateId = state.open ? state.gateId : null;

  useEffect(() => {
    setIsValid(false);
    setPosition('');
  }, [state]);

  const onSubmit = useCallback(
    (needConfirmation?: boolean) => {
      if (!isValid || !gateId) return;
      const positionNumber = parseFloat(position);
      if (isNaN(positionNumber)) return;
      if (needConfirmation) {
        setOpenConfirmation(true);
      } else {
        moveGate(gateId, positionNumber);
      }
    },
    [isValid, position, gateId, moveGate],
  );

  return (
    <GlobalModal
      {...globalModalProps}
      dialogProps={{ maxWidth: 'md' }}
      authorization={{ access: Access.Allow }}
    >
      {gateId && (
        <ConfirmationModal
          open={openConfirmation}
          onClose={() => setOpenConfirmation(false)}
          title={<DeviceNameSpan deviceId={gateId}></DeviceNameSpan>}
          content={
            <Trans>
              ¿Esta seguro que desea mover la compuerta desde{' '}
              <b style={{ color: 'white' }}>{formatPosition(initialPosition, formatMetricValue)}</b>{' '}
              a{' '}
              <b style={{ color: GREEN }}>{formatPosition(Number(position), formatMetricValue)}</b>?
            </Trans>
          }
          submitButton={{
            onClick: () => onSubmit(),
            color: 'success',
            loading,
          }}
        />
      )}
      <ModalTitle
        onClose={onClose}
        title={<Trans>Definir operación</Trans>}
        subtitle={
          gateId ? (
            <>
              {gateId} - <DeviceNameSpan deviceId={gateId} />
            </>
          ) : (
            <Trans>Sin compuerta seleccionada</Trans>
          )
        }
      />
      <ModalContent sx={{ display: 'grid', gap: 2 }}>
        <Scenario />
        <OperatingState gateId={gateId} />
        <LastPosition gateId={gateId} />
        <MoveGateForm
          gateId={gateId}
          setIsValid={setIsValid}
          position={position}
          setPosition={setPosition}
          setInitialPosition={setInitialPosition}
        />
      </ModalContent>
      <ModalActions
        onClose={onClose}
        onSubmit={() => onSubmit(true)}
        submitColor="success"
        submitDisabled={!isValid || isNaN(parseFloat(position))}
        submitLabel={_(msg`Operar compuerta`)}
        closeButton={{ label: _(msg`Cancelar`) }}
      />
    </GlobalModal>
  );
};
